/**
 * 示例组件 2：IconButton（T04）。
 *
 * 与 Button 同构，但默认 44 x 44 —— 触摸目标最小尺寸，方便安卓移植时
 * 直接映射成 ImageButton / 自绘 IconButton。
 */
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';

import { buildComponentMeta, type ComponentMetaInput } from './meta';
import { useMeasuredComponentMeta } from './registry';

export const ICON_BUTTON_DEFAULT_SIZE = { width: 44, height: 44 } as const;

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'style' | 'children' | 'aria-label'
> {
  /** 组件元数据输入；未显式给 layout 宽高时按 44 x 44 处理。 */
  meta: ComponentMetaInput;
  frameRef?: React.RefObject<HTMLElement | null>;
  /** 图标内容（svg / 字符均可）。 */
  icon: ReactNode;
}

/** 正方形图标按钮，默认 44 x 44 触摸目标。 */
export function IconButton({ meta, frameRef, icon, type = 'button', ...rest }: IconButtonProps) {
  const layout = {
    ...meta.layout,
    width: meta.layout.width || ICON_BUTTON_DEFAULT_SIZE.width,
    height: meta.layout.height || ICON_BUTTON_DEFAULT_SIZE.height,
  };
  const componentMeta = buildComponentMeta({ ...meta, layout }, 'button');
  const ref = useMeasuredComponentMeta<HTMLButtonElement>(componentMeta, frameRef);

  const style: CSSProperties = {
    position: 'absolute',
    left: `${layout.x}px`,
    top: `${layout.y}px`,
    width: `${layout.width}px`,
    height: `${layout.height}px`,
    zIndex: componentMeta.zIndex,
  };

  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      aria-label={componentMeta.label}
      style={style}
    >
      {icon}
    </button>
  );
}
